import React, { useCallback, useEffect, useState } from "react";
import { fetchDocumentTypes } from "./fetchDocumentTypes";
import { fetchPatientData } from "./fetchPatientData";
import { useAuth } from "./authContext";
import { updateUserSettings, getUserSettings } from "./firestoreService";
import { clearCachedData } from "./backendHealth";
import { getAuthHeaders } from "./apiUtils";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

const MANAGE_OPTION_VALUE = "__manage_document_types__";
export { MANAGE_OPTION_VALUE };

export async function fetchManageDocumentTypes() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch('/api/v3/configuration/document-types', {
    credentials: 'include',
    headers: {
      ...authHeaders,
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Unable to load document types (HTTP ${response.status})`);
  }

  const data = await response.json();
  if (!Array.isArray(data)) {
    throw new Error('Document types response was not an array');
  }

  return [...data].sort((a, b) => (
    (a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' })
  ));
}

export function ManageDocumentTypesModal({ open, onClose, userId, currentSettings, onSaved }) {
  const [allTypes, setAllTypes] = useState([]);
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const data = await fetchManageDocumentTypes();
        if (cancelled) return;
        setAllTypes(data);
        const availableIds = new Set(data.map(type => type?.id).filter(Boolean));
        const initialHidden = new Set(
          Array.isArray(currentSettings?.hiddenDocumentTypeIds)
            ? currentSettings.hiddenDocumentTypeIds.filter(id => availableIds.has(id))
            : []
        );
        setHiddenIds(initialHidden);
        setSaveError("");
      } catch (err) {
        console.error('[ManageDocumentTypesModal] Failed to load types', err);
        if (!cancelled) {
          setAllTypes([]);
          setHiddenIds(new Set());
          setLoadError("Document types couldn't be loaded. Please close this window and try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, currentSettings]);

  const toggle = (id) => {
    setSaveError("");
    setHiddenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const showAll = () => {
    setSaveError("");
    setHiddenIds(new Set());
  };
  const hideAll = () => {
    setSaveError("At least one document type must remain visible.");
    setHiddenIds(new Set(allTypes.map(t => t.id)));
  };

  const handleSave = async () => {
    if (!userId) { onClose(); return; }
    if (allTypes.length > 0 && hiddenIds.size >= allTypes.length) {
      setSaveError("At least one document type must remain visible.");
      return;
    }
    setSaving(true);
    try {
      const availableIds = new Set(allTypes.map(type => type.id));
      const unavailableHiddenIds = (currentSettings?.hiddenDocumentTypeIds || []).filter(id => !availableIds.has(id));
      await updateUserSettings(userId, { hiddenDocumentTypeIds: [...unavailableHiddenIds, ...hiddenIds] });
      clearCachedData();
      if (onSaved) await onSaved();
    } catch (err) {
      console.error('[ManageDocumentTypesModal] Save failed', err);
    } finally {
      setSaving(false);
      onClose();
    }
  };

  const selectedCount = allTypes.length - hiddenIds.size;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Documents</DialogTitle>
      <DialogContent dividers>
        <div style={{ marginBottom: 12, fontSize: 14, color: '#555' }}>
          Choose which document types appear in your menus.
          {' '}<strong>{selectedCount}</strong> of <strong>{allTypes.length}</strong> selected.
          <div>Beta types require beta access. Summary types appear under Generate Summary.</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <Button size="small" onClick={showAll} disabled={loading || Boolean(loadError) || hiddenIds.size === 0}>Show all</Button>
          <Button size="small" onClick={hideAll} disabled={loading || Boolean(loadError) || allTypes.length === 0 || hiddenIds.size === allTypes.length}>Hide all</Button>
        </div>
        {loadError && (
          <div style={{ marginBottom: 12, color: '#b91c1c', fontSize: 13 }} role="alert">
            {loadError}
          </div>
        )}
        {saveError && (
          <div style={{ marginBottom: 12, color: '#b91c1c', fontSize: 13 }} role="alert">
            {saveError}
          </div>
        )}
        {loading ? (
          <div>Loading…</div>
        ) : (
          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {allTypes.length === 0 && !loadError ? (
              <div>No document types are currently available for your company.</div>
            ) : allTypes.map(t => {
              const checked = !hiddenIds.has(t.id);
              const summaryOnly = t.category === 3;
              return (
                <label key={t.id} style={{ display: 'flex', alignItems: 'center', padding: '6px 4px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(t.id)}
                    style={{ marginRight: 8 }}
                  />
                  <span>
                    {t.name}
                    {summaryOnly && <small style={{ display: 'block' }}>Use Generate Summary</small>}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loading || Boolean(loadError) || allTypes.length === 0}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function useDocumentTypeSelection(patientId) {
  const { currentUser } = useAuth();
  const userId = currentUser?.uid;

  const [documentTypes, setDocumentTypes] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState("");
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [eta, setEta] = useState(null);
  const [patientData, setPatientData] = useState({});
  const [patientDocuments, setPatientDocuments] = useState([]);
  const [userSettings, setUserSettings] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(0);

  useEffect(() => {
    let active = true;
    setDocumentTypes([]);
    setUserSettings(null);
    if (userId) {
      (async () => {
        let settings = null;
        try { settings = await getUserSettings(userId); } catch { /* menu policy is server-enforced */ }
        if (!active) return;
        setUserSettings(settings);
        await fetchDocumentTypes(types => { if (active) setDocumentTypes(types); },
          undefined, undefined, undefined, undefined, null, settings);
      })();
    }
    return () => { active = false; };
  }, [userId, refreshVersion]);

  useEffect(() => {
    const refresh = () => setRefreshVersion(version => version + 1);
    window.addEventListener('documentTypesUpdated', refresh);
    return () => window.removeEventListener('documentTypesUpdated', refresh);
  }, []);

  const refreshPatientData = useCallback(async () => {
    if (!patientId) {
      setPatientData({});
      setPatientDocuments([]);
      return { patientData: {}, patientDocuments: [] };
    }

    return fetchPatientData(patientId, setPatientData, setPatientDocuments);
  }, [patientId]);

  useEffect(() => {
    refreshPatientData();
  }, [refreshPatientData]);

  // Listen for patient updates
  useEffect(() => {
    const handlePatientUpdate = (event) => {
      const { patientId: updatedPatientId, patientData: updatedPatientData } = event.detail || {};

      if (updatedPatientId === patientId) {
        setPatientData(updatedPatientData);
      }
    };

    window.addEventListener('patientUpdated', handlePatientUpdate);

    return () => {
      window.removeEventListener('patientUpdated', handlePatientUpdate);
    };
  }, [patientId]);

  useEffect(() => {
    const selected = documentTypes.find(type => type.id === userSettings?.documentType) || documentTypes[0];
    setSelectedDocumentType(selected?.name || '');
    setSelectedDocumentTypeId(selected?.id || '');
    setSelectedCategoryId(selected?.categoryId || selected?.category || '');
    setEta(selected?.eta || null);
  }, [documentTypes, userSettings]);

  const handleDocumentTypeChange = (e) => {
    const selectedTypeName = e.target.value;
    const selectedType = documentTypes.find((type) => type.name === selectedTypeName);

    if (selectedType) {
      setSelectedDocumentType(selectedType.name);
      setSelectedDocumentTypeId(selectedType.id);
      if (selectedType.categoryId) setSelectedCategoryId(selectedType.categoryId);
      else setSelectedCategoryId("");
      if (selectedType.eta) setEta(selectedType.eta);
      else setEta(null);

      if (userId) {
        updateUserSettings(userId, { documentType: selectedType.id });
      }
    }
  };

  // Function to manually refresh user settings
  const refreshUserSettings = async () => {
    if (userId) {
      try {
        const settings = await getUserSettings(userId);
        setUserSettings(settings);
      } catch (error) {
        console.error(`[documentTypeSelection] Error refreshing user settings`, error);
      }
    }
  };

  return {
    documentTypes,
    selectedDocumentType,
    selectedDocumentTypeId,
    selectedCategoryId,
    eta,
    handleDocumentTypeChange,
    patientData,
    patientDocuments,
    refreshPatientData,
    userSettings,
    refreshUserSettings,
  };
}
